<template>
  <teleport to="#modals">
    <vue-resizable
      v-if="dealActivityOptions.state === DealActivityState.Draggable"
      data-cy="deal-notes-draggable"
      class="z-101"
      drag-selector=".handle"
      :width="dealActivityOptions.width"
      :height="dealActivityOptions.height"
      :top="dealActivityOptions.top"
      :left="dealActivityOptions.left"
      :min-width="notesWidthAndHeight.minWidth"
      :min-height="notesWidthAndHeight.minHeight"
      @mount="updatePosition"
      @resize:end="updatePosition"
      @drag:end="updatePosition"
    >
      <div
        ref="draggable"
        class="w-full rounded-md border border-gray-300 bg-white shadow-lg"
        :class="
          dealActivityOptions.minimized ? 'h-[55px]' : 'h-full overflow-hidden'
        "
      >
        <div data-cy="handle" class="handle absolute top-[18px] left-4">
          <icon-base
            :icon="IconCardView"
            width="16"
            height="16"
            view-box="0 0 24 24"
            class="handle rotate-90 transform cursor-grab"
          />
        </div>
        <deal-activity-content />
      </div>
    </vue-resizable>
    <vue-resizable
      v-else-if="dealActivityOptions.state === DealActivityState.Static"
      class="absolute z-100"
      :width="sidebarWidth"
      height="100%"
      :top="0"
      :left="offsetLeft"
      :min-width="MIN_WIDTH"
      :max-width="maxWidth"
      :active="['l']"
      drag-selector=".no-drag"
      @resize:end="updateWidth"
    >
      <div class="no-drag hidden" />
      <div
        data-cy="deal-notes-static"
        class="h-screen w-full bg-white py-4 shadow-2xl"
      >
        <deal-activity-content />
      </div>
    </vue-resizable>
  </teleport>
</template>
<script setup lang="ts">
import { computed, reactive, ref, useTemplateRef, watch } from "vue";
import VueResizable from "vue-resizable";

import { storeToRefs } from "pinia";
import queryString from "query-string";

import { DealActivityState } from "@/enums/dealActivity";
import { DEFAULT_DEAL_ACTIVITY_OPTIONS } from "@/helpers/constants/deals";
import { useDeals } from "@/hooks/deals";
import { useLocalStorageSetting } from "@/hooks/options";
import { useResizable, useResizableActivitySidebar } from "@/hooks/resizable";
import useNotesStore from "@/stores/notes";

import DealActivityContent from "@/components/deals/dealActivity/DealActivityContent.vue";
import IconCardView from "@/components/icons/IconCardView.vue";

const initialParams = queryString.parse(location.search);
const { activeDeal } = useDeals();
const notesStore = useNotesStore();
const dealActivityOptions = useLocalStorageSetting(
  "dealActivityOptions",
  DEFAULT_DEAL_ACTIVITY_OPTIONS
);

const timeouts = {
  scrollToNote: 1500,
  highlightNote: 300,
  removeHighlightNote: 3000,
} as const;

const MIN_WIDTH = 500;

const { currentNotes: dealNotes } = storeToRefs(notesStore);

const draggable = useTemplateRef("draggable");
const { updatePosition } = useResizable(dealActivityOptions, draggable);

const notesWidthAndHeight = ref({
  width: DEFAULT_DEAL_ACTIVITY_OPTIONS.width,
  height: DEFAULT_DEAL_ACTIVITY_OPTIONS.height,
  minWidth: MIN_WIDTH,
  minHeight: 250,
});

const { maxWidth, offsetLeft, updateWidth, sidebarWidth } =
  useResizableActivitySidebar({
    minWidth: MIN_WIDTH,
    key: "dealActivityWidth",
  });

const params = reactive({
  noteId: initialParams.note || null,
});

const numOfNotes = computed(() => dealNotes.value?.data?.length ?? 0);

const scrollNoteIntoView = () => {
  const note = document.getElementById(
    params.noteId as string
  ) as HTMLDivElement;

  if (!note) {
    return;
  }

  note.scrollIntoView({
    block: "start",
    inline: "nearest",
    behavior: "smooth",
  });

  setTimeout(() => note.classList.add("highlight"), timeouts.highlightNote);

  setTimeout(() => {
    note.classList.remove("highlight");
  }, timeouts.removeHighlightNote);
};

watch(
  [activeDeal, params, numOfNotes],
  () => {
    if (activeDeal.value && params.noteId && numOfNotes.value) {
      const isNoteInDeal = dealNotes.value?.data?.some(
        (note) => note.id === Number(params.noteId)
      );

      if (
        dealActivityOptions.value.state === DealActivityState.Draggable ||
        !isNoteInDeal
      ) {
        return;
      }

      if (dealActivityOptions.value.state !== DealActivityState.Closed) {
        setTimeout(scrollNoteIntoView, timeouts.scrollToNote);
      }
    }
  },
  { immediate: true }
);
</script>
